import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGenres } from '../../hooks/useGenres';
import { DownArrowIcon } from '../Icons/DownArrowIcon';

interface GenreDropdownProps {
  selectedGenre: string;
  onGenreChange: (genre: string) => void;
  className?: string;
  size?: 'small' | 'large'; // small for home sections, large for detail pages
  category?: string; // music, serenity, book
  genres?: string[]; // Optional: pass genres directly to avoid API call (used for home page)
}

// Inner component that uses the hook (only rendered when genres are not passed)
const GenreDropdownWithHook = memo(
  ({
    selectedGenre,
    onGenreChange,
    className,
    size,
    category,
  }: Omit<GenreDropdownProps, 'genres'>) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch genres from API (for detail pages)
    const { genres: fetchedGenres, loading } = useGenres(category);
    const genres = fetchedGenres;
    const isLoading = loading;

    return (
      <GenreDropdownContent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dropdownRef={dropdownRef}
        selectedGenre={selectedGenre}
        onGenreChange={onGenreChange}
        genres={genres}
        isLoading={isLoading}
        className={className}
        size={size}
      />
    );
  },
);

GenreDropdownWithHook.displayName = 'GenreDropdownWithHook';

// Inner component that doesn't use the hook (rendered when genres are passed)
const GenreDropdownWithoutHook = memo(
  ({
    selectedGenre,
    onGenreChange,
    className,
    size,
    genres,
  }: Omit<GenreDropdownProps, 'category'> & { genres: string[] }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    return (
      <GenreDropdownContent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dropdownRef={dropdownRef}
        selectedGenre={selectedGenre}
        onGenreChange={onGenreChange}
        genres={genres}
        isLoading={false}
        className={className}
        size={size}
      />
    );
  },
);

GenreDropdownWithoutHook.displayName = 'GenreDropdownWithoutHook';

// Shared content component
const GenreDropdownContent = memo(
  ({
    isOpen,
    setIsOpen,
    dropdownRef,
    selectedGenre,
    onGenreChange,
    genres,
    isLoading,
    className,
    size,
  }: {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    selectedGenre: string;
    onGenreChange: (genre: string) => void;
    genres: string[];
    isLoading: boolean;
    className?: string;
    size?: 'small' | 'large';
  }) => {
    const { t } = useTranslation();
    // Debug logging
    useEffect(() => {
      console.log(
        'GenreDropdown mounted, genres:',
        genres.length,
        'loading:',
        isLoading,
      );
    }, [genres.length, isLoading]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleGenreSelect = (genre: string) => {
      onGenreChange(genre);
      setIsOpen(false);
    };

    const displayGenre = selectedGenre || 'All';

    // Always include 'All' as the first option, then add other genres
    const genreOptions = ['All', ...genres.filter((g) => g !== 'All')];

    // Dynamic styling based on size
    const buttonHeight = size === 'small' ? 'h-[32px]' : 'h-[48px]';
    const buttonWidth = 'w-[160px]';
    const textSize = size === 'small' ? 'text-xs' : 'text-sm';
    const padding = size === 'small' ? 'px-3 py-1' : 'px-4 py-2';

    return (
      <div ref={dropdownRef} className={`relative inline-block ${className}`}>
        {/* Dropdown Button - Dynamic sizing */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className={`flex items-center capitalize justify-between text-white/80 ${textSize} font-medium leading-[130%] ${buttonHeight} border-1 border-white/20 rounded-full ${padding} hover:bg-white/10 focus:outline-none transition-colors duration-200 ${buttonWidth}`}>
          <span className="truncate">
            {isLoading ? t('genre.loading') : displayGenre}
          </span>
          <span className="pl-1">
            <DownArrowIcon />
          </span>
        </button>

        {/* Dropdown Menu - Higher z-index to appear above cards */}
        {isOpen && !isLoading && (
          <div className="absolute z-[99999] w-full mt-1 bg-black border border-white/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {genreOptions.map((genre: string) => (
              <button
                key={genre}
                onClick={() => handleGenreSelect(genre)}
                className={`w-full capitalize px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg ${
                  genre === displayGenre
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80'
                }`}>
                {genre}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

GenreDropdownContent.displayName = 'GenreDropdownContent';

// Main export - conditionally renders with or without hook based on whether genres are passed
export const GenreDropdown = memo(
  ({
    selectedGenre,
    onGenreChange,
    className = '',
    size = 'large',
    category = 'music',
    genres: passedGenres,
  }: GenreDropdownProps) => {
    // Check if genres are explicitly passed (even if empty array, it means we're on home page)
    // We use a special marker to distinguish between "not passed" and "passed but empty"
    const hasGenresProp = passedGenres !== undefined;
    const hasGenresData = passedGenres && passedGenres.length > 0;

    // If genres prop is passed (home page), render without hook to avoid API calls
    // Use 'All' as default if genres are empty (loading state)
    if (hasGenresProp) {
      return (
        <GenreDropdownWithoutHook
          selectedGenre={selectedGenre}
          onGenreChange={onGenreChange}
          className={className}
          size={size}
          genres={hasGenresData ? passedGenres : ['All']}
        />
      );
    }

    // Otherwise, render with hook to fetch genres from API (detail pages)
    return (
      <GenreDropdownWithHook
        selectedGenre={selectedGenre}
        onGenreChange={onGenreChange}
        className={className}
        size={size}
        category={category}
      />
    );
  },
);

GenreDropdown.displayName = 'GenreDropdown';
